/**
 * Shared notification helpers — navigation, grouping, filters.
 */

export const NOTIFICATION_QUERY_KEYS = {
  list: ['notifications'],
  unreadCount: ['notifications', 'unread-count'],
};

/** Invalidate notification + conversation caches after marketplace mutations. */
export function invalidateNotificationQueries(queryClient) {
  queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.list });
  queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.unreadCount });
  queryClient.invalidateQueries({ queryKey: ['conversations'] });
}

export const NOTIFICATION_FILTERS = [
  { id: 'all', types: null },
  { id: 'orders', types: ['order'] },
  { id: 'offers', types: ['offer', 'bid'] },
  { id: 'messages', types: ['message'] },
  { id: 'system', types: ['account', 'product', 'promotional', 'system'] },
];

export function getNotificationBody(n) {
  return n?.body || n?.message || '';
}

export function matchesNotificationFilter(notification, filterId) {
  const filter = NOTIFICATION_FILTERS.find((f) => f.id === filterId);
  if (!filter || !filter.types) return true;
  return filter.types.includes(notification.type);
}

export function groupNotificationsByDate(notifications) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);

  const groups = { today: [], yesterday: [], older: [] };

  notifications.forEach((n) => {
    const d = new Date(n.createdAt);
    if (d >= startOfToday) groups.today.push(n);
    else if (d >= startOfYesterday) groups.yesterday.push(n);
    else groups.older.push(n);
  });

  return groups;
}

/**
 * Build react-router navigation target from notification.
 */
export function getNotificationNavigation(notification) {
  const data = notification?.data || notification?.payload || {};
  const actionUrl = notification?.actionUrl || notification?.link;

  if (actionUrl) {
    if (actionUrl.startsWith('/product/')) {
      return { pathname: actionUrl };
    }
    if (actionUrl.startsWith('/orders/')) {
      const orderId = actionUrl.split('/').filter(Boolean)[1];
      if (orderId) {
        return { pathname: '/profile', search: `?tab=orders&orderId=${encodeURIComponent(orderId)}` };
      }
    }
    if (actionUrl.includes('tab=orders')) {
      return { pathname: '/profile', search: '?tab=orders' };
    }
    if (actionUrl.includes('tab=bids')) {
      return { pathname: '/profile', search: '?tab=bids' };
    }
    if (actionUrl === '/conversations' || actionUrl.startsWith('/conversations')) {
      return {
        pathname: '/conversations',
        state: {
          conversationId: notification.conversationId || data.conversationId,
          productId: data.productId,
          openOffer:
            notification.type === 'offer' ||
            notification.type === 'bid' ||
            data.type?.includes?.('offer'),
        },
      };
    }
    if (actionUrl === '/carte') {
      return { pathname: '/carte' };
    }
    return { pathname: actionUrl };
  }

  if (data.orderId || notification.entityType === 'order') {
    return { pathname: '/profile', search: '?tab=orders' };
  }

  if (
    notification.type === 'message' ||
    notification.type === 'offer' ||
    notification.type === 'bid' ||
    data.conversationId ||
    data.offerId ||
    data.bidId
  ) {
    return {
      pathname: '/conversations',
      state: {
        conversationId: notification.conversationId || data.conversationId,
        productId: data.productId,
        openOffer: notification.type === 'offer' || notification.type === 'bid',
      },
    };
  }

  if (data.productId) {
    return { pathname: `/product/${data.productId}` };
  }

  return { pathname: '/profile' };
}

const toastDedupe = new Map();
const TOAST_WINDOW_MS = 8000;

/**
 * Show grouped in-app toast for a new notification (avoids spam).
 */
export function showNotificationToast(notification, showNotification) {
  if (!showNotification || !notification) return;

  const data = notification.data || {};
  const storeKey = data.conversationId
    ? `conv:${data.conversationId}:${notification.type}`
    : `id:${notification._id || notification.id}`;

  const now = Date.now();
  const prev = toastDedupe.get(storeKey);
  if (prev && now - prev.at < TOAST_WINDOW_MS) {
    prev.count += 1;
    prev.at = now;
    toastDedupe.set(storeKey, prev);
    showNotification({
      type: 'info',
      title: notification.title,
      message:
        prev.count > 1
          ? `${prev.count} updates — ${getNotificationBody(notification)}`
          : getNotificationBody(notification),
      duration: 4000,
    });
    return;
  }

  toastDedupe.set(storeKey, { count: 1, at: now });
  showNotification({
    type: notification.type === 'order' ? 'success' : 'info',
    title: notification.title,
    message: getNotificationBody(notification),
    duration: 4500,
  });
}
